type WebshopLocale = "en" | "nl" | "de";

type TranslationKeys =
  | "storefront.product.sortNewest"
  | "storefront.product.tagFilterLabel"
  | "storefront.product.tagFilterPlaceholder"
  | "storefront.product.priceFilterLabel"
  | "storefront.cart.openCart"
  | "storefront.header.menu"
  | "storefront.topBar.socialAria"
  | "storefront.product.shareOnFacebook"
  | "storefront.product.shareOnX"
  | "storefront.product.shareOnWhatsApp"
  | "webshop.navAllIn";

const SSR_LEAKED_TRANSLATIONS: Record<WebshopLocale, Record<TranslationKeys, string>> = {
  en: {
    "storefront.product.sortNewest": "Newest first",
    "storefront.product.tagFilterLabel": "Tag",
    "storefront.product.tagFilterPlaceholder": "Filter by tag",
    "storefront.product.priceFilterLabel": "Price",
    "storefront.cart.openCart": "Open shopping cart",
    "storefront.header.menu": "Menu",
    "storefront.topBar.socialAria": "Follow us on social media",
    "storefront.product.shareOnFacebook": "Share on Facebook",
    "storefront.product.shareOnX": "Share on X",
    "storefront.product.shareOnWhatsApp": "Share via WhatsApp",
    "webshop.navAllIn": "All in",
  },
  nl: {
    "storefront.product.sortNewest": "Nieuwste eerst",
    "storefront.product.tagFilterLabel": "Tag",
    "storefront.product.tagFilterPlaceholder": "Filter op tag",
    "storefront.product.priceFilterLabel": "Prijs",
    "storefront.cart.openCart": "Open winkelwagen",
    "storefront.header.menu": "Menu",
    "storefront.topBar.socialAria": "Volg ons op social media",
    "storefront.product.shareOnFacebook": "Deel op Facebook",
    "storefront.product.shareOnX": "Deel op X",
    "storefront.product.shareOnWhatsApp": "Deel via WhatsApp",
    "webshop.navAllIn": "Alles in",
  },
  de: {
    "storefront.product.sortNewest": "Neueste zuerst",
    "storefront.product.tagFilterLabel": "Tag",
    "storefront.product.tagFilterPlaceholder": "Nach Tag filtern",
    "storefront.product.priceFilterLabel": "Preis",
    "storefront.cart.openCart": "Warenkorb öffnen",
    "storefront.header.menu": "Menü",
    "storefront.topBar.socialAria": "Folge uns in den sozialen Medien",
    "storefront.product.shareOnFacebook": "Auf Facebook teilen",
    "storefront.product.shareOnX": "Auf X teilen",
    "storefront.product.shareOnWhatsApp": "Per WhatsApp teilen",
    "webshop.navAllIn": "Alle in",
  },
};

const TRANSLATION_KEY_PATTERN =
  /^(storefront\.product\.sortNewest|storefront\.product\.tagFilterLabel|storefront\.product\.tagFilterPlaceholder|storefront\.product\.priceFilterLabel|storefront\.cart\.openCart|storefront\.header\.menu|storefront\.topBar\.socialAria|storefront\.product\.shareOnFacebook|storefront\.product\.shareOnX|storefront\.product\.shareOnWhatsApp|webshop\.navAllIn)$/;

function isSupportedLocale(value: string): value is WebshopLocale {
  return value === "en" || value === "nl" || value === "de";
}

function detectLocale(): WebshopLocale {
  const tenantLocale = (
    window as Window & { __TENANT__?: { locale?: string | null } }
  ).__TENANT__?.locale;
  const tenantShort = tenantLocale?.substring(0, 2).toLowerCase();
  if (tenantShort && isSupportedLocale(tenantShort)) {
    return tenantShort;
  }

  const htmlShort = document.documentElement.lang?.substring(0, 2).toLowerCase();
  if (htmlShort && isSupportedLocale(htmlShort)) {
    return htmlShort;
  }

  return "en";
}

function replaceTextIfKey(
  node: Element | null,
  translations: Record<TranslationKeys, string>,
): void {
  if (!(node instanceof HTMLElement)) return;
  const key = node.textContent?.trim();
  if (!key || !TRANSLATION_KEY_PATTERN.test(key)) return;
  const translated = translations[key as TranslationKeys];
  if (translated) {
    node.textContent = translated;
  }
}

function replaceAttrIfKey(
  node: Element | null,
  attr: "placeholder" | "aria-label",
  translations: Record<TranslationKeys, string>,
): void {
  if (!(node instanceof HTMLElement)) return;
  const key = node.getAttribute(attr)?.trim();
  if (!key || !TRANSLATION_KEY_PATTERN.test(key)) return;
  const translated = translations[key as TranslationKeys];
  if (translated) {
    node.setAttribute(attr, translated);
  }
}

/**
 * Rewrites raw i18n keys in legacy/stale SSR HTML before interactive mount.
 */
export function patchStorefrontSsrTranslationLeaks(root: ParentNode = document): void {
  const locale = detectLocale();
  const translations = SSR_LEAKED_TRANSLATIONS[locale];

  root.querySelectorAll<HTMLElement>("[aria-label]").forEach((node) => {
    replaceAttrIfKey(node, "aria-label", translations);
  });

  root.querySelectorAll<HTMLElement>("[placeholder]").forEach((node) => {
    replaceAttrIfKey(node, "placeholder", translations);
  });

  root.querySelectorAll<HTMLElement>("option, label, .price-range-slider__title, .nav-dropdown-item--all").forEach((node) => {
    replaceTextIfKey(node, translations);
  });
}
